import React, { memo, useMemo, useCallback } from "react";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import TitleIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Certificate_Title.png.svg";
import DescriptionIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Certificate_Description.svg";
import FromDateIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/From_Date.svg";
import ToDateIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/To_Date.svg";
import ActionIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Action.svg";

const EmpCertificationTable = memo(({
  data = [],
  canEdit = false,
  onEdit,
  noDataMessage = "No Certifications Available",
}) => {
  const columns = useMemo(() => [
    { 
      key: "Title", 
      label: "Title", 
      icon: TitleIcon,
      width: '25%'
    },
    { 
      key: "Description", 
      label: "Description", 
      icon: DescriptionIcon,
      width: '35%'
    },
    { 
      key: "ValidFrom", 
      label: "Valid From", 
      icon: FromDateIcon,
      isDate: true,
      center: true,
      width: '15%'
    },
    { 
      key: "ValidTill", 
      label: "Valid Till", 
      icon: ToDateIcon,
      isDate: true,
      center: true,
      width: '15%'
    },
    { 
      key: "action", 
      label: "Action", 
      icon: ActionIcon, 
      isAction: true,
      center: true,
      width: '10%'
    },
  ], []);

  const formatDate = useCallback((date) => {
    return date ? moment(date).format("DD-MMM-YYYY") : "";
  }, []);

  const renderTooltip = useCallback((text) => <Tooltip>{text}</Tooltip>, []);

  const renderCellContent = useCallback((text, isDate = false, center = false) => {
    const displayText = isDate ? formatDate(text) : text || "";
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={renderTooltip(displayText)}
      >
        <div 
          className={`text-truncate ${center ? 'text-center' : ''}`} 
          style={{ 
            width: '100%',
            display: 'inline-block'
          }}
        >
          {displayText}
        </div>
      </OverlayTrigger>
    );
  }, [formatDate, renderTooltip]);

  const handleEditClick = useCallback((item) => {
    if (canEdit && onEdit) {
      onEdit(item);
    }
  }, [canEdit, onEdit]);

  return (
    <div className="certification-table-container">
      <style>
        {`
          .certification-table-container {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .fixed-certification-table {
            table-layout: fixed;
            width: 100%;
            min-width: 700px; /* Minimum width to ensure columns don't collapse */
          }
          
          .fixed-width-column {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @media (min-width: 992px) {
            .fixed-certification-table {
              min-width: 100%;
            }
          }
          
          .tech-icon {
            width: 20px;
            height: 20px;
          }
        `}
      </style>
      
      <Table striped bordered hover className="fixed-certification-table">
        <colgroup>
          {columns.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead className="text-center">
          <tr>
            {columns.map((column) => (
              <th 
                key={column.key}
                style={{ width: column.width }}
              >
                <div className="d-flex flex-column align-items-center fixed-width-column">
                  <div>
                    <img
                      src={column.icon}
                      alt={column.label}
                      className="tech-icon"
                    />
                  </div>
                  <div style={{ fontSize: "12px" }}>{column.label}</div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => {
                  if (column.isAction) {
                    return (
                      <td 
                        key={column.key} 
                        className="text-center fixed-width-column"
                        style={{ width: column.width }}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{canEdit ? "Edit" : "Edit disabled"}</Tooltip>}
                        >
                          <span
                            onClick={() => handleEditClick(item)}
                            style={{
                              cursor: canEdit ? "pointer" : "not-allowed",
                              opacity: canEdit ? 1 : 0.5,
                            }}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </span>
                        </OverlayTrigger>
                      </td>
                    );
                  }
                  
                  return (
                    <td 
                      key={column.key} 
                      className={`fixed-width-column ${column.center ? "text-center" : ""}`}
                      style={{ width: column.width }}
                    >
                      {renderCellContent(
                        item[column.key], 
                        column.isDate, 
                        column.center
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center">
                {noDataMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
});

export default EmpCertificationTable;